import type { Quotation } from '@/api/quotations';
import { Button, Card, CardContent, Input } from '@/components/ui';
import { useQuotations } from '@/hooks/use-quotations';
import { useServerPagination } from '@/hooks/use-server-pagination';
import { useTranslation } from '@kgc/i18n';
import { DataTable } from '@kgc/ui';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QUOTATION_STATUSES } from './mock-data';
import type { QuotationStatus } from './types';

export function QuotationListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<QuotationStatus | 'all'>('all');

  // Server-mode pagination (BE-S2 = A): a lista eddig page/pageSize nélkül hívott
  // → CSAK a BE-default első ~20 árajánlatot mutatta (a 21+ némán láthatatlan volt).
  const serverPagination = useServerPagination(20);

  const { quotations, total, isLoading, error } = useQuotations({
    search: searchTerm || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    ...serverPagination.query,
  });

  // Szűrő-váltáskor ugorj az 1. oldalra.
  useEffect(() => {
    serverPagination.reset();
  }, [searchTerm, statusFilter, serverPagination.reset]);

  const filteredQuotations = useMemo(() => {
    // Server-side filtering is used; client-side fallback for immediate UI response
    return quotations;
  }, [quotations]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('hu-HU');
  };

  const getStatusBadge = (status: QuotationStatus) => {
    const config = QUOTATION_STATUSES.find(s => s.value === status);
    return (
      <span className={`rounded-full px-2 py-1 text-xs font-medium ${config?.color ?? ''}`}>
        {config ? t(config.labelKey) : status}
      </span>
    );
  };

  const isExpiringSoon = (validUntil: string) => {
    const daysUntilExpiry = Math.ceil(
      (new Date(validUntil).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );
    return daysUntilExpiry > 0 && daysUntilExpiry <= 3;
  };

  // Oszlop-definíciók a megosztott DataTable-höz (a cella-renderek megőrzik az eredeti markup-ot).
  const columns: ColumnDef<Quotation>[] = [
    {
      id: 'quotation',
      header: t('service.quote'),
      cell: ({ row }) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-gray-100">
            {row.original.quotationNumber}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {row.original.items.length} {t('quotation.itemsCount')}
          </p>
        </div>
      ),
    },
    {
      id: 'partner',
      header: t('table.partner'),
      cell: ({ row }) => (
        <div>
          <p className="text-gray-900 dark:text-gray-100">{row.original.partnerName}</p>
          {row.original.partnerEmail && (
            <p className="text-sm text-gray-500 dark:text-gray-400">{row.original.partnerEmail}</p>
          )}
        </div>
      ),
    },
    {
      id: 'worksheet',
      header: t('quotation.worksheet'),
      cell: ({ row }) =>
        row.original.worksheetNumber ? (
          <button
            onClick={e => {
              e.stopPropagation();
              navigate(`/worksheet/${row.original.worksheetId}`);
            }}
            className="text-blue-600 hover:underline dark:text-blue-400"
          >
            {row.original.worksheetNumber}
          </button>
        ) : (
          <span className="text-gray-400 dark:text-gray-300">-</span>
        ),
    },
    {
      id: 'createdAt',
      header: t('quotation.createdAt'),
      cell: ({ row }) => (
        <span className="text-gray-600 dark:text-gray-300">
          {formatDate(row.original.createdAt)}
        </span>
      ),
    },
    {
      id: 'validUntil',
      header: t('quotation.validUntil'),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span className="text-gray-600 dark:text-gray-300">
            {formatDate(row.original.validUntil)}
          </span>
          {row.original.status === 'sent' && isExpiringSoon(row.original.validUntil) && (
            <span className="rounded bg-orange-100 px-1.5 py-0.5 text-xs font-medium text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
              {t('quotation.expiringSoon')}
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'amount',
      header: () => <div className="text-right">{t('quotation.amount')}</div>,
      cell: ({ row }) => (
        <div className="text-right">
          <p className="font-medium text-gray-900 dark:text-gray-100">
            {formatPrice(row.original.totalGross)}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('quotation.gross')}</p>
        </div>
      ),
    },
    {
      id: 'status',
      header: () => <div className="text-center">{t('table.status')}</div>,
      cell: ({ row }) => <div className="text-center">{getStatusBadge(row.original.status)}</div>,
    },
  ];

  // Stats (computed from loaded data)
  const stats = useMemo(
    () => ({
      total: quotations.length,
      draft: quotations.filter(q => q.status === 'draft').length,
      sent: quotations.filter(q => q.status === 'sent').length,
      accepted: quotations.filter(q => q.status === 'accepted').length,
      totalValue: quotations
        .filter(q => q.status === 'sent')
        .reduce((sum, q) => sum + q.totalGross, 0),
    }),
    [quotations]
  );

  if (error) {
    return (
      <div className="kgc-bg flex min-h-screen items-center justify-center">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen kgc-bg">
      {isLoading && (
        <div className="py-4 text-center text-sm text-muted-foreground">{t('common.loading')}</div>
      )}
      {/* Header */}
      <header className="shadow-sm kgc-card-bg">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {t('nav.quotes')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('service.subtitle')}</p>
            </div>
          </div>
          <Button onClick={() => navigate('/worksheet')} className="bg-primary hover:bg-primary/90">
            {t('nav.worksheets')}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Stats cards */}
        <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-5">
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.total}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.all')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">{stats.draft}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('quotation.draft')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.sent}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('quotation.sent')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {stats.accepted}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('quotation.accepted')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {formatPrice(stats.totalValue)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('quotation.pendingValue')}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="min-w-[250px] flex-1">
                <Input
                  placeholder={t('invoice.searchPlaceholder')}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as QuotationStatus | 'all')}
                className="rounded-md border px-3 py-2 border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100"
              >
                <option value="all">{t('page.allStatuses')}</option>
                {QUOTATION_STATUSES.map(s => (
                  <option key={s.value} value={s.value}>
                    {t(s.labelKey)}
                  </option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Quotations list */}
        <Card>
          <CardContent className="p-0">
            <DataTable
              columns={columns}
              data={filteredQuotations}
              emptyMessage={t('page.noFilterResults')}
              getRowId={quotation => quotation.id}
              onRowClick={quotation => navigate(`/quotations/${quotation.id}`)}
              {...(typeof total === 'number' ? serverPagination.tableProps(total) : {})}
              rowActions={quotation => (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={e => {
                    e.stopPropagation();
                    navigate(`/quotations/${quotation.id}`);
                  }}
                >
                  {t('common.details')}
                </Button>
              )}
            />
          </CardContent>
        </Card>

        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          {filteredQuotations.length} {t('quotation.quotationsDisplayed')}
        </p>

        {/* Info box */}
        <Card className="mt-6 border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">💡</span>
              <div>
                <p className="font-medium text-blue-800 dark:text-blue-200">
                  {t('quotation.newQuotationInfo')}
                </p>
                <p className="text-sm text-blue-600 dark:text-blue-300">
                  {t('quotation.newQuotationDesc')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
